import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface DailyTrendChartProps {
  title: string;
  data: { date: string; count: number }[];
  maxBars?: number;
  emptyMessage?: string;
}

export const DailyTrendChart = ({
  title,
  data: rawData,
  maxBars = 14,
  emptyMessage = 'No data for the selected period.',
}: DailyTrendChartProps) => {
  // Sample evenly if more data points than maxBars
  const data =
    rawData.length <= maxBars
      ? rawData
      : Array.from({ length: maxBars }, (_, i) => {
          const idx = Math.round((i * (rawData.length - 1)) / (maxBars - 1));
          return rawData[idx];
        });

  const maxCount = Math.max(...data.map((d) => d.count), 1);

  const formatDate = (dateStr: string) => {
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const labelStep = data.length <= 12 ? 1 : Math.ceil(data.length / 10);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{title}</CardTitle>
        {rawData.length > 0 && (
          <span className="text-muted-foreground text-xs font-normal">
            {rawData.length > maxBars
              ? `Showing ${maxBars} of ${rawData.length} days`
              : `${rawData.length} day${rawData.length === 1 ? '' : 's'}`}
          </span>
        )}
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <p className="text-muted-foreground py-6 text-center text-sm">{emptyMessage}</p>
        ) : (
          <div>
            {/* Chart area */}
            <div className="flex items-end gap-1" style={{ height: 200 }}>
              {data.map((item) => {
                const heightPct = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                return (
                  <div
                    key={item.date}
                    className="group relative flex flex-1 items-end justify-center"
                    style={{ height: '100%' }}
                  >
                    {/* Tooltip */}
                    <div className="bg-popover text-popover-foreground pointer-events-none absolute -top-8 left-1/2 z-10 hidden -translate-x-1/2 rounded-md border px-2 py-1 text-xs whitespace-nowrap shadow-md group-hover:block">
                      {formatDate(item.date)}: {item.count}
                    </div>
                    {/* Bar */}
                    <div
                      className="w-full rounded-t-sm bg-blue-500 transition-all duration-300 hover:bg-blue-400"
                      style={{
                        height: `${Math.max(heightPct, 2)}%`,
                        minWidth: 3,
                      }}
                    />
                  </div>
                );
              })}
            </div>
            {/* X-axis labels */}
            <div className="mt-2 flex gap-1">
              {data.map((item, idx) => (
                <div key={item.date} className="flex-1 text-center">
                  {idx % labelStep === 0 ? (
                    <span className="text-muted-foreground text-[10px] leading-tight">
                      {formatDate(item.date)}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
